import { AxiosError } from 'axios'
import { helpers } from '@berkshireassociates/berkshirecomponents'
import type { AxiosResponse } from 'axios'
import { getApiEndpoint } from '~/helpers/api'
import { handleError } from '~/helpers/handleError'
import { PATH, USER_FACING_ERROR_MESSAGES } from '~/constants/strings'
import { api } from '~/services'
import { notifyFailure } from '~/utils/notifications'

export { handleError, helpers }

export const splitLongWords = helpers.filters.splitLongWords
export const chunkArray = helpers.array.chunkArray
export const titleCaseString = helpers.filters.titleCaseString
export const combinePathAndQueryStrings = helpers.url.combinePathAndQueryStrings
export const convertMegabytesToBytes = helpers.file.convertMegabytesToBytes
export const formatBytes = helpers.file.formatBytes
export const getFileNamesAndSizes = helpers.file.getFileNamesAndSizes
export const deepFind = helpers.filters.deepFind
export const getQueryString = helpers.url.getQueryString
export const friendlyPercentOfTotal = helpers.filters.friendlyPercentOfTotal
export const isNumeric = helpers.filters.isNumeric
export const findObjectByKeyValue = helpers.array.findObjectByKeyValue
export const toCamelCase = helpers.filters.toCamelCase
export const extractNonNumericStrings = helpers.filters.extractNonNumericStrings
export const isBerkshireEmailAddress = helpers.filters.isBerkshireEmailAddress
export const replaceString = helpers.filters.replaceString
export const blobToDataURL = helpers.file.blobToDataURL
export const getEmailFullDomain = helpers.email.getEmailFullDomain
export const getEmailName = helpers.email.getEmailName
export const getEmailDomain = helpers.email.getEmailDomain
export const getEmailTopLevelDomain = helpers.email.getEmailTopLevelDomain
export const capitalizeFirstLetter = helpers.filters.capitalizeFirstLetter
export const mapOneProperty = helpers.array.mapOneProperty
export const getUniqueListBy = helpers.array.getUniqueListBy
export const redirectToNewSubdomain = helpers.redirect.redirectToNewSubdomain
export const pathWithTargetSubdomain = helpers.redirect.pathWithTargetSubdomain
export const cookies = helpers.cookies.default

// TODO: update to use crypto.randomUUID
// https://developer.mozilla.org/en-US/docs/Web/API/Crypto/randomUUID
export const uuid = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.trunc(Math.random() * 16),
      v = c == 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export const randomItemFromArray = (items: Array<any>) =>
  items[Math.floor(Math.random() * items.length)]

export const randomName = () => {
  const names = [
    'people',
    'history',
    'way',
    'art',
    'world',
    'information',
    'map',
    'family',
    'government',
    'health',
    'system',
    'computer',
    'meat',
    'year',
    'thanks',
    'music',
    'person',
    'reading',
    'method',
    'data',
    'food',
    'understanding',
    'theory',
    'law',
    'bird',
    'literature',
    'problem',
    'software',
    'control',
    'knowledge',
    'power',
    'ability',
    'economics',
    'love',
    'internet',
    'television',
    'science',
    'library',
    'nature',
    'fact',
    'product',
    'idea',
    'temperature',
    'investment',
    'area',
    'society',
    'activity',
    'story',
    'industry',
    'media',
    'thing',
    'oven',
    'community',
    'definition',
    'safety',
    'quality',
    'development',
    'language',
    'management',
    'player',
    'variety',
    'video',
    'week',
    'security',
    'country',
    'exam',
    'movie',
    'organization',
    'equipment',
    'physics',
    'analysis',
    'policy',
  ]
  return randomItemFromArray(names)
}

export const randomEmail = () =>
  `${randomName()}@${randomName()}.${randomItemFromArray([
    'com',
    'org',
    'net',
  ])}`

/**
 *
 * @param enumObject The Enumeration object
 * @param value The stringified number or key you need to find
 */
// TODO: Allow actual names to be passed e.g. getEnumValue(myEnum, 'someEnumThatExistsInMyEnum'), also figure out how to make a param accept enums
export const getEnumValue = (
  enumObject: any,
  value: number | string | null | undefined
): any | null => {
  return getEnum(enumObject, value)?.[1] ?? null
}

/**
 *
 * @param enumObject The Enumeration object
 * @param value The stringified number or key you need to find
 * @returns the enum as key value pair [key, value] or null
 */
//
export const getEnum = (
  enumObject: any,
  value: number | string | null | undefined
): [string, any] | null => {
  if (!value && ![0, '0'].includes(value as string | number)) return null
  const enumEntries = Object.entries(enumObject)
  const enumValue = enumEntries.find((k) => k[1] === Number(value))
  return enumValue ?? null
}

export const getURL = (path: string): string => {
  return `${getApiEndpoint()}/api${path}`
}

const handleNetworkRequestErrors = <T>({
  error,
  shouldErrorRedirect,
}: {
  error: any | AxiosError<T>
  shouldErrorRedirect?: boolean
}): AxiosError<T | void> => {
  if (error instanceof AxiosError) {
    // console.log(`HNRV2 ${error.request.responseURL}`, { error })
    handleError(error, shouldErrorRedirect ?? true)
    return error
  }

  notifyFailure(USER_FACING_ERROR_MESSAGES.SERVER_ERROR)
  console.error('Unexpected error:', { error })
  return new AxiosError<void>('Unexpected error', 'NETWORK REQUEST FAILURE')
}

type BCPAxiosRequestFunction<T> = () => Promise<AxiosResponse<T, any>>
export type BCPAxiosResponse<T> = Promise<T | AxiosError<T | void>>
type BCPFileAxiosResponse<T> = Promise<AxiosResponse<T> | AxiosError<T | void>>

export const HNRV2 = async <T>({
  fn,
  shouldErrorRedirect,
}: {
  fn: BCPAxiosRequestFunction<T>
  shouldErrorRedirect?: boolean
}): BCPAxiosResponse<T> => {
  try {
    const response = await fn()
    return response.data
  } catch (error: any | AxiosError<T>) {
    return handleNetworkRequestErrors({ error, shouldErrorRedirect })
  }
}

export const fileNetworkRequest = async <T>({
  fn,
  shouldErrorRedirect,
}: {
  fn: BCPAxiosRequestFunction<T>
  shouldErrorRedirect?: boolean
}): BCPFileAxiosResponse<T> => {
  try {
    const response = await fn()
    return response
  } catch (error: any | AxiosError<T>) {
    return handleNetworkRequestErrors({ error, shouldErrorRedirect })
  }
}

export const switchToClient = async ({
  clientID,
  clientSubdomain,
  newTab,
}: {
  clientID: string
  clientSubdomain: string
  newTab?: boolean
}) => {
  try {
    await api.users.apiIdentityUsersSwitchClientClientIDPut(clientID)
    redirectToNewSubdomain({
      newSubdomain: clientSubdomain,
      newPath: PATH.SUBDOMAIN_REDIRECTION,
      newTab,
    })
  } catch (error) {
    console.error(error)
  }
}

export const getUnreactiveCopyOfArray = (array: any[]): any[] => {
  return array.map((value) => {
    return JSON.parse(JSON.stringify(value))
  })
}

/**
 * Creates a debounced version of a function that delays its execution until after a specified time has passed since the last time it was invoked.
 *
 * @param {F} fn - The function to be debounced.
 * @param {number} time - The time in milliseconds to wait before executing the debounced function.
 * @return {(...args: Parameters<F>) => void} - A debounced version of the input function.
 */
export const debounce = <F extends (...args: any[]) => any>(
  fn: F,
  time: number
): ((...args: Parameters<F>) => void) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null
  return (...args: Parameters<F>) => {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    timeoutId = setTimeout(() => {
      fn(...args)
    }, time)
  }
}
